import { apiGet, apiPost } from './client';

export interface IngestionJobProgress {
  total: number;
  done: number;
  failed: number;
}

export interface IngestionJob {
  id: string;
  connector_id: string;
  job_type: string;
  status: string;
  progress: IngestionJobProgress;
  attempts: number;
  max_attempts: number;
  result: Record<string, unknown> | null;
  error: Record<string, unknown> | null;
  created_at: string | null;
  started_at: string | null;
  finished_at: string | null;
}

export interface IngestFileResult {
  status: string;
  resource_id: string;
  external_resource_id: string;
  chunk_count: number;
  source_filename: string;
  extracted_chars: number;
}

export async function getIngestionJobs(status?: string): Promise<{ jobs: IngestionJob[]; total: number }> {
  const params = status ? { status } : undefined;
  const res = await apiGet<{ data: IngestionJob[]; meta: { total: number } }>(
    '/v1/ingest/jobs',
    params,
  );
  return { jobs: res.data, total: res.meta.total };
}

export function getIngestionJob(id: string): Promise<IngestionJob> {
  return apiGet<IngestionJob>(`/v1/ingest/jobs/${id}`);
}

export function cancelIngestionJob(id: string): Promise<IngestionJob> {
  return apiPost<IngestionJob>(`/v1/ingest/jobs/${id}/cancel`);
}

/** Upload a file for ingestion (multipart; not the JSON client helpers). */
export async function uploadIngestFile(
  file: File,
  connectorId: string,
  options: { classification?: string; sensitivity?: string; domain?: string } = {},
): Promise<IngestFileResult> {
  const base = import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api` : '/api';
  const form = new FormData();
  form.append('file', file);
  form.append('connector_id', connectorId);
  if (options.classification) form.append('classification', options.classification);
  if (options.sensitivity) form.append('sensitivity', options.sensitivity);
  if (options.domain) form.append('domain', options.domain);

  const token = localStorage.getItem('access_token');
  const response = await fetch(`${base}/v1/ingest/file`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: form,
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.detail || `Upload failed (${response.status})`);
  }
  return data as IngestFileResult;
}
